import { HttpClient } from '@angular/common/http';
import { computed, effect, Inject, Injectable, OnDestroy, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ApiResponse, NotificationType } from '@app/shared/interfaces';
import { EventSourceService } from '@app/shared/services/event-source/event-source.service';
import { PresenceService } from '@app/shared/services/presence/presence.service';
import { PresenceResourceType, PresenceType } from '@app/shared/services/pusher/pusher.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { AIMessage } from '@app/shared/services/thread/thread.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { Observable, of, SubscriptionLike, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

// Define StreamEventDataType enum
export enum StreamEventDataType {
  UPDATE = 'update',
  ERROR = 'error',
  FAIL = 'fail',
  FINISH = 'finish',
  INFO = 'info',
}
// Define StreamEventContent interface
export interface StreamEventContent {
  reply?: string;
  error?: string;
  uuid?: string;
  model?: string;
  prompt_version?: string;
}

// Define StreamEvent interface
export interface StreamEvent {
  type: StreamEventDataType;
  content: StreamEventContent;
}

// Define GeneratedReply interface
export interface GeneratedReply {
  uuid: string;
  reply: string;
  model?: string;
  prompt_version?: string;
}

// States for AI operations
export enum AIState {
  Idle = 'idle',
  Suggesting = 'suggesting',
  Improving = 'improving',
  FetchingPrewritten = 'fetching_prewritten',
  UpdatingPrewritten = 'updating_prewritten',
  Error = 'error',
  Success = 'success',
}

// Generation types
export enum GenerationType {
  Suggest = 'suggest',
  Improve = 'improve',
  Prewritten = 'prewritten',
  ManualEdit = 'manual_edit',
}

// Complete generation history item
export interface AIGeneration {
  id: string; // Local ID for this generation in history
  type: GenerationType;
  reply: string;
  originalContent?: string;
  timestamp: Date;
  model?: string;
  promptVersion?: string;
  scheduledAtLabel?: string | null;
  apiUuid?: string; // UUID from the API if applicable
  undone?: boolean; // Add this flag to track if a generation was undone
}

@Injectable({
  providedIn: 'root',
})
export class InboxAIService implements OnDestroy {
  // State signals
  private $generations = signal<AIGeneration[]>([]);
  private $state = signal<AIState>(AIState.Idle);
  private $error = signal<string | null>(null);

  // Progress message for streaming
  private $progressMessage = signal<string | null>(null);

  // Add a public accessor for progressMessage
  public readonly $progressMessageSignal = this.$progressMessage.asReadonly();

  // Computed signals for component consumption
  public currentGeneration = computed(() => {
    const gens = this.$generations();
    // Find the last generation that hasn't been undone
    for (let i = gens.length - 1; i >= 0; i--) {
      if (!gens[i].undone) {
        return gens[i];
      }
    }
    // No active generations
    return null;
  });

  public readonly currentGeneration$ = toObservable(this.currentGeneration);

  public firstGeneration = computed(() => {
    for (let i = 0; i < this.$generations().length; i++) {
      const gen = this.$generations()[i];

      if (gen?.type !== GenerationType.Suggest && gen?.type !== GenerationType.Improve) {
        continue;
      }

      return gen;
    }

    return null;
  });

  // Convenience method to check if current generation is a prewritten message
  public isPrewrittenMessage = computed(() => {
    const current = this.currentGeneration();
    return current?.type === GenerationType.Prewritten;
  });

  public isSuggesting = computed(() => this.$state() === AIState.Suggesting);
  public isImproving = computed(() => this.$state() === AIState.Improving);
  public isFetchingPrewritten = computed(() => this.$state() === AIState.FetchingPrewritten);
  public isUpdatingPrewritten = computed(() => this.$state() === AIState.UpdatingPrewritten);
  public isIdle = computed(() => this.$state() === AIState.Idle);
  public isError = computed(() => this.$state() === AIState.Error);
  public isSuccess = computed(() => this.$state() === AIState.Success);
  public isWorking = computed(() => [AIState.Idle, AIState.Success, AIState.Error].indexOf(this.$state()) === -1);

  // New signal to track if AI has provided a suggestion in the current session
  public hasAiSuggested = computed(() => {
    return this.$generations().some(
      (gen) => (gen.type === GenerationType.Suggest || gen.type === GenerationType.Improve) && !gen.undone
    );
  });

  // Capabilities
  public canSuggest = computed(() => {
    // Cannot suggest if an operation is in progress
    if (this.isWorking()) return false;

    // Cannot suggest if the last action was fetching a prewritten message
    const current = this.currentGeneration();

    // If there's no active generation or it's been improved, allow suggesting
    if (!current || this.$generations().some((g) => g.type === GenerationType.Improve && !g.undone)) {
      return true;
    }

    // Cannot suggest after prewritten unless it's been undone
    return current.type !== GenerationType.Prewritten;
  });

  public canImprove = computed(() => {
    // Can improve if not busy
    return !this.isWorking();
  });

  // New properties for AI presence
  private aiPresence = signal<{
    working: boolean;
    messageUuid: string | null;
    workingMessage: string;
    isCancelling: boolean;
  }>({
    working: false,
    messageUuid: null,
    workingMessage: 'Auto Reply AI is thinking',
    isCancelling: false,
  });

  // AI summary generation signal
  private aiSummaryGenerating = signal<boolean>(false);
  private aiSummaryFailed = signal<boolean>(false);

  // Public computed values for AI presence
  public aiPresenceWorking = computed(() => this.aiPresence().working);
  public aiPresenceMessageUuid = computed(() => this.aiPresence().messageUuid);
  public aiPresenceWorkingMessage = computed(() => this.aiPresence().workingMessage);
  public aiPresenceIsCancelling = computed(() => this.aiPresence().isCancelling);

  // Public getter for AI summary generation
  public isGeneratingSummary = computed(() => this.aiSummaryGenerating());
  public isSummaryFailed = computed(() => this.aiSummaryFailed());

  private eventSourceSubscription: SubscriptionLike | null = null;

  constructor(
    @Inject('apiUrl') private apiUrl: string,
    private http: HttpClient,
    private eventSourceService: EventSourceService,
    private toast: ToastNotificationsService,
    private presenceService: PresenceService,
    @Inject(SegmentIoService) private segmentIoService?: SegmentIoService
  ) {
    // Initial emission
    this.currentGeneration();

    // Easy debugging:
    const debug = false;
    if (debug) {
      effect(() => {
        console.log('--------------------------------');
        console.log('firstSuggestion', this.firstGeneration());
        console.log('currentGeneration', this.currentGeneration());
        console.log('generations', this.$generations());
        console.log('state', this.$state());
        console.log('error', this.$error());
        console.log('canSuggest', this.canSuggest());
        console.log('canImprove', this.canImprove());
        console.log('--------------------------------');
      });
    }
  }

  // Add a generation to history and update state
  private addGeneration(generation: AIGeneration): void {
    this.$generations.update((gens) => [...gens, generation]);
    this.$state.set(AIState.Success);
    this.$error.set(null);
  }

  // Set the state and optional error message
  private setState(newState: AIState, errorMessage: string | null = null): void {
    this.$state.set(newState);
    if (errorMessage) {
      this.$error.set(errorMessage);
    } else if (newState !== AIState.Error) {
      this.$error.set(null);
    }
  }

  /**
   * Completely resets all AI state and history.
   *
   * This is different from undo() in that it removes ALL history including analytics tracking.
   * Use this when:
   * - Starting a new thread/conversation
   * - The user sends a message (analytics should already be tracked by then)
   * - Component unmounts
   *
   * Do NOT use this for simple undo operations where you want to preserve analytics - use undo() instead.
   */
  public reset(): void {
    this.$generations.set([]);
    this.$state.set(AIState.Idle);
    this.$error.set(null);
    this.$progressMessage.set(null);
    this.resetAIPresence();
    this.aiSummaryGenerating.set(false);
    this.aiSummaryFailed.set(false);
    this.closeStream();
  }

  // Stream suggestion
  public streamSuggestWithAI(threadUuid: string, hostUid: string | null): Observable<string> {
    if (!this.canSuggest()) {
      return throwError(() => new Error('Cannot start suggestion: operation in progress or prewritten message active'));
    }

    this.setState(AIState.Suggesting);
    this.$progressMessage.set(''); // Set initial progress message

    const previousGenUuid = this.firstGeneration()?.apiUuid || null;

    return this.authenticateForStream(threadUuid, hostUid, previousGenUuid).pipe(
      switchMap((url) => {
        let accumulatedText = '';
        return new Observable<string>((observer) => {
          this.eventSourceSubscription = this.eventSourceService
            .connectToServerSentEvents(url, {}, ['update'])
            .subscribe({
              next: (event) => {
                if (event.data === '</stream>') {
                  this.finishStreaming(accumulatedText, null);
                  observer.complete();
                  return;
                }

                try {
                  const data: StreamEvent = JSON.parse(event.data);

                  // Generating message
                  if (data.type === StreamEventDataType.UPDATE) {
                    this.$progressMessage.set('');
                    accumulatedText += data.content.reply;
                    observer.next(accumulatedText);
                    return;
                  }

                  // Finish event
                  if (data.type === StreamEventDataType.FINISH) {
                    this.finishStreaming(accumulatedText, data);
                    observer.complete();
                  }

                  // Errors will be retried automatically, do not change state
                  if (data.type === StreamEventDataType.ERROR) {
                    this.$progressMessage.set('Error, retrying');
                    accumulatedText = '';
                    observer.next(accumulatedText);
                    return;
                  }

                  // If a fail occurred, show an error to the user
                  if (data.type === StreamEventDataType.FAIL) {
                    this.setState(AIState.Error, 'Failed to generate AI suggestion');
                    this.$progressMessage.set(null);
                    this.toast.open(
                      'An error occurred processing this action - please try again. If this keeps occurring, please contact support',
                      'Dismiss',
                      NotificationType.Error
                    );
                    observer.error(new Error('Failed to generate reply'));
                    return;
                  }
                } catch (e) {
                  observer.error(new Error('Failed to parse JSON'));
                }
              },
              error: (error) => {
                this.setState(AIState.Error, 'Stream error occurred');
                this.$progressMessage.set('Error occurred');
                this.toast.open(
                  'An error occurred processing this action - please try again. If this keeps occurring, please contact support',
                  'Dismiss',
                  NotificationType.Error
                );
                observer.error(error);
                this.closeStream();
              },
            });

          return () => {
            this.closeStream();
          };
        });
      }),
      catchError((error) => {
        this.setState(AIState.Error, 'Failed to authenticate for streaming');
        this.$progressMessage.set('Authentication failed');
        return throwError(() => error);
      })
    );
  }

  private finishStreaming(accumulatedText: string, data: StreamEvent | null): void {
    this.$progressMessage.set(null); // Clear progress message on completion
    const newGeneration: AIGeneration = {
      id: uuidv4(),
      type: GenerationType.Suggest,
      reply: accumulatedText,
      promptVersion: data?.content.prompt_version,
      model: data?.content.model,
      timestamp: new Date(),
      apiUuid: data?.content.uuid,
    };
    this.addGeneration(newGeneration);
  }

  // Non-streaming suggest
  public suggestWithAI(threadUuid: string, hostUid: string | null): Observable<AIGeneration> {
    if (!this.canSuggest()) {
      return throwError(() => new Error('Cannot start suggestion: operation in progress or prewritten message active'));
    }

    this.setState(AIState.Suggesting);

    return this.http
      .post<
        ApiResponse<GeneratedReply>
      >(`${this.apiUrl}/threads/${threadUuid}/generate-reply`, { host_id: hostUid, generated_reply_id: this.firstGeneration()?.apiUuid || null })
      .pipe(
        map((response) => {
          const generation: AIGeneration = {
            id: uuidv4(),
            type: GenerationType.Suggest,
            reply: response.data.reply,
            originalContent: response.data.reply,
            timestamp: new Date(),
            model: response.data.model,
            promptVersion: response.data.prompt_version,
            apiUuid: response.data.uuid,
          };
          this.addGeneration(generation);
          return generation;
        }),
        catchError((error) => {
          this.setState(AIState.Error, 'Failed to generate AI suggestion');
          return throwError(() => error);
        })
      );
  }

  // Improve message
  public improveReply(threadUuid: string, existingMessage: string): Observable<AIGeneration> {
    if (!this.canImprove()) {
      return throwError(() => new Error('Cannot start improvement: operation in progress or no content to improve'));
    }

    this.setState(AIState.Improving);

    return this.http
      .post<
        ApiResponse<GeneratedReply>
      >(`${this.apiUrl}/threads/${threadUuid}/generate-improvement`, { message: existingMessage })
      .pipe(
        map((response) => {
          const generation: AIGeneration = {
            id: uuidv4(),
            type: GenerationType.Improve,
            reply: response.data.reply,
            timestamp: new Date(),
            model: response.data.model,
            promptVersion: response.data.prompt_version,
            apiUuid: response.data.uuid,
          };
          this.addGeneration(generation);
          return generation;
        }),
        catchError((error) => {
          this.setState(AIState.Error, 'Failed to improve message');
          return throwError(() => error);
        })
      );
  }

  private addPrewrittenGeneration(message: AIMessage): void {
    const generation: AIGeneration = {
      id: uuidv4(),
      type: GenerationType.Prewritten,
      reply: message.content,
      timestamp: new Date(),
      scheduledAtLabel: message.scheduled_at_label,
      originalContent: message.content,
      apiUuid: message.uuid,
    };
    this.addGeneration(generation);

    // Track that a prewritten message was viewed
    if (this.segmentIoService) {
      this.segmentIoService.track(SegmentEvent.AIPrewrittenDraftMessageViewed, {});
    }
  }

  public setPrewrittenMessage(message: AIMessage): void {
    this.addPrewrittenGeneration(message);
  }

  // Get prewritten message - now adds it directly to generations
  public getPrewrittenMessage(threadUuid: string): void {
    this.setState(AIState.FetchingPrewritten);

    this.http
      .get<ApiResponse<AIMessage>>(`${this.apiUrl}/threads/${threadUuid}/ai/prewritten`)
      .pipe(
        map((response) => {
          this.setState(AIState.Idle);

          if (!response.data) {
            return;
          }

          this.addPrewrittenGeneration(response.data);
        })
      )
      .subscribe();
  }

  // Update prewritten message (send now or cancel)
  public updatePrewrittenMessage(
    prewrittenMessageUuid: string,
    action: 'send_now' | 'cancel',
    currentMessage: string
  ): Observable<boolean> {
    if (this.isWorking()) {
      return throwError(() => new Error('Operation in progress'));
    }

    this.setState(AIState.UpdatingPrewritten);

    const payload = {
      send_now: action === 'send_now',
      cancel: action === 'cancel',
      message: currentMessage,
    };

    return this.http.put<void>(`${this.apiUrl}/gx/ai/message/${prewrittenMessageUuid}`, payload).pipe(
      map(() => {
        // Mark the prewritten message as undone because `cancel` means
        // that the AI reply offered is not what the host intended,
        // which is equivalent to a failed Suggest with AI.
        // `send_now` will reset the service upstream anyway after it's
        // done.
        this.$generations.update((gens) =>
          gens.map((g) => (g.apiUuid === prewrittenMessageUuid ? { ...g, undone: true } : g))
        );

        return true;
      }),
      catchError((error) => {
        this.setState(AIState.Error, `Failed to ${action === 'send_now' ? 'send' : 'cancel'} prewritten message`);
        return of(false);
      })
    );
  }

  public addManualEdit(message: string): void {
    const updatedGenerations = [...this.$generations()];
    let latestGen = null;
    let index = null;
    for (index = updatedGenerations.length - 1; index >= 0; index--) {
      if (updatedGenerations[index].undone) {
        continue;
      }

      latestGen = updatedGenerations[index];
      break;
    }

    if (latestGen) {
      updatedGenerations[index] = { ...latestGen, reply: message };
      this.$generations.set(updatedGenerations);

      return;
    }

    const generation: AIGeneration = {
      id: uuidv4(),
      type: GenerationType.ManualEdit,
      timestamp: new Date(),
      reply: message,
    };

    this.addGeneration(generation);
  }

  /**
   * Undoes the most recent AI generation by marking it as undone rather than removing it completely.
   * This preserves the generation history for analytics purposes while allowing the user to revert
   * to a previous state. When a generation is undone:
   *
   * 1. It remains in the generations array with undone=true
   * 2. It won't be returned by currentGeneration() but is still accessible for analytics
   * 3. firstGeneration() can be used to access the first suggestion for tracking
   *
   * @returns The previous non-undone generation, or null if all have been undone
   */
  public undo(): AIGeneration | null {
    const gens = [...this.$generations()];
    if (gens.length === 0) return null;

    // If only one item, mark it as undone but keep it for analytics
    if (gens.length === 1) {
      const lastGen = gens[0];
      const updatedGen = { ...lastGen, undone: true };
      this.$generations.set([updatedGen]);
      this.setState(AIState.Idle);
      return null; // Return null to indicate nothing to revert to
    }

    // If we have multiple generations, mark the most recent NOT undone as undone
    // and return it. The idea here is that the UI is looking at the current
    // generation, which is defined as the latest non-undone generation.
    let latestNotUndoneGen = null;
    let index = null;
    for (index = gens.length - 1; index >= 0; index--) {
      if (!gens[index].undone) {
        latestNotUndoneGen = gens[index];
        break;
      }
    }

    if (!latestNotUndoneGen) {
      return null;
    }

    gens[index] = { ...latestNotUndoneGen, undone: true };
    this.$generations.set(gens);

    this.setState(AIState.Idle);

    for (let i = gens.length - 1; i >= 0; i--) {
      if (!gens[i].undone) {
        return gens[i];
      }
    }

    return null;
  }

  // Helper methods
  private authenticateForStream(
    threadUuid: string,
    hostUid: string | null,
    generatedReplyUuid: string | null
  ): Observable<string> {
    return this.http
      .post<ApiResponse<{ url: string }>>(`${this.apiUrl}/authenticate-stream`, {
        route_type: 'stream-reply',
        resource_id: threadUuid,
        host_id: hostUid,
        generated_reply_id: generatedReplyUuid,
      })
      .pipe(map((res) => res.data.url));
  }

  private closeStream(): void {
    if (this.eventSourceSubscription) {
      this.eventSourceSubscription.unsubscribe();
      this.eventSourceSubscription = null;
      this.eventSourceService.close();
    }
  }

  // Cancel an AI message generation in progress
  public cancelAIPresenceMessage(uuid: string): Observable<boolean> {
    const url = `${this.apiUrl}/gx/ai/message/${uuid}`;
    return this.http.delete(url).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  // Add undoneGenerations for analytics purposes
  public undoneGenerations = computed(() => this.$generations().filter((gen) => gen.undone === true));

  // Utility methods for text handling
  public sanitizeText(text: string | null | undefined): string | null {
    if (text === null || text === undefined) {
      return null;
    }
    return text
      .replace(/[\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Checks if a given message matches the current generation
   * @param message The message to check
   * @returns True if the message matches the current AI generation
   */
  public isGeneratedReply(message: string): boolean {
    const current = this.currentGeneration();
    if (!current) return false;

    if (current.type === GenerationType.Suggest || current.type === GenerationType.Improve) {
      return this.sanitizeText(current.reply) === this.sanitizeText(message);
    }
    return false;
  }

  /**
   * Sends a prewritten message immediately
   * @param messageContent The message content to send
   * @returns Observable that emits true on success, false on failure
   */
  public sendPrewrittenMessageNow(messageContent: string): Observable<boolean> {
    const currentGen = this.currentGeneration();
    if (!currentGen || currentGen.type !== GenerationType.Prewritten) {
      return of(false);
    }

    return this.updatePrewrittenMessage(currentGen.apiUuid || currentGen.id, 'send_now', messageContent);
  }

  /**
   * Cancels a prewritten message
   * @param messageContent The current message content
   * @returns Observable that emits true on success, false on failure
   */
  public cancelPrewrittenMessage(messageContent: string): Observable<boolean> {
    const currentGen = this.currentGeneration();
    if (!currentGen || currentGen.type !== GenerationType.Prewritten) {
      return of(false);
    }

    return this.updatePrewrittenMessage(currentGen.apiUuid || currentGen.id, 'cancel', messageContent);
  }

  /**
   * Listen for AI presence updates for a thread
   * @param threadUuid The thread UUID to monitor for AI presence
   * @returns Observable that completes when the subscription is done
   */
  public listenForAIPresence(threadUuid: string): Observable<void> {
    return this.presenceService.getAIActingInThread(threadUuid).pipe(
      map((aiUserPresence) => {
        if (!aiUserPresence) {
          this.updateAIPresence({ working: false, messageUuid: null });
          this.aiSummaryGenerating.set(false); // Reset summary generation
          this.aiSummaryFailed.set(false);
          return;
        }

        // Store the message UUID if it exists in metadata
        if (aiUserPresence.metadata?.ai_message_uuid) {
          this.updateAIPresence({ messageUuid: aiUserPresence.metadata.ai_message_uuid });
        }

        switch (aiUserPresence.type) {
          case PresenceType.InboxAIDecidingHowToProceed:
            this.updateAIPresence({
              working: true,
              workingMessage: 'Auto Reply AI is thinking',
            });
            return;
          case PresenceType.InboxAIWritingAutoReply:
          case PresenceType.InboxAIAssessingAutoReply:
            this.updateAIPresence({
              working: true,
              workingMessage: 'Auto Reply AI is writing',
            });
            return;
          case PresenceType.InboxAIWritingAutoReplyPrepared:
            this.updateAIPresence({ working: false });
            // Automatically fetch the prewritten message
            this.getPrewrittenMessage(threadUuid);
            return;
          case PresenceType.InboxAIWritingAutoReplySucceeded:
          case PresenceType.InboxAIWritingAutoReplyFailed:
          case PresenceType.InboxAIWritingAutoReplyAborted:
            this.updateAIPresence({
              working: false,
              messageUuid: null,
            });
            return;
          case PresenceType.InboxAIGeneratingThreadSummary:
            this.aiSummaryGenerating.set(true);
            return;
          case PresenceType.InboxAIGeneratingThreadSummarySucceeded:
            this.aiSummaryGenerating.set(false);
            this.aiSummaryFailed.set(false);
            return;
          case PresenceType.InboxAIGeneratingThreadSummaryFailed:
            this.aiSummaryGenerating.set(false);
            this.aiSummaryFailed.set(true);
            return;
          default:
            this.updateAIPresence({
              working: false,
              messageUuid: null,
            });
            return;
        }
      })
    );
  }

  /**
   * Update the AI presence state
   * @param update Partial update to apply to the presence state
   */
  private updateAIPresence(
    update: Partial<{
      working: boolean;
      messageUuid: string | null;
      workingMessage: string;
      isCancelling: boolean;
    }>
  ): void {
    this.aiPresence.update((current) => ({ ...current, ...update }));
  }

  /**
   * Cancel an AI presence operation
   * @param threadUuid The thread UUID
   * @param segmentIoService The segment service for tracking
   * @returns Observable that emits true on success, false on failure
   */
  public cancelAIPresence(threadUuid: string, segmentIoService: SegmentIoService): Observable<boolean> {
    const messageUuid = this.aiPresenceMessageUuid();

    if (!messageUuid || this.aiPresenceIsCancelling()) {
      return of(false);
    }

    this.updateAIPresence({ isCancelling: true });

    return this.cancelAIPresenceMessage(messageUuid).pipe(
      tap((success) => {
        this.updateAIPresence({ isCancelling: false });
        if (success) {
          segmentIoService.track(SegmentEvent.AICancelledAutoReply, {
            thread_uuid: threadUuid,
            message_uuid: messageUuid,
          });
          this.updateAIPresence({ messageUuid: null });
        }
      }),
      catchError((err) => {
        this.updateAIPresence({ isCancelling: false });
        return of(false);
      })
    );
  }

  /**
   * Reset AI presence state
   */
  public resetAIPresence(): void {
    this.aiPresence.set({
      working: false,
      messageUuid: null,
      workingMessage: 'Auto Reply AI is thinking',
      isCancelling: false,
    });
  }

  /**
   * Send a typing presence update to indicate the user is typing in a thread
   * @param threadUuid The thread UUID
   * @returns Observable with the result of sending the presence update
   */
  public sendTypingPresenceUpdate(threadUuid: string): Observable<any> {
    try {
      return this.presenceService.sendPresenceUpdate(
        PresenceType.InboxTypingMessage,
        threadUuid,
        PresenceResourceType.Conversations
      );
    } catch (error) {
      console.error('Error sending typing presence update:', error);
      return of(null);
    }
  }

  /**
   * Send an idle presence update when leaving a thread
   * @returns Observable with the result of sending the presence update
   */
  public sendIdlePresenceUpdate(): Observable<any> {
    try {
      return this.presenceService.sendPresenceUpdate(PresenceType.Idle, null, PresenceResourceType.Conversations);
    } catch (error) {
      console.error('Error sending idle presence update:', error);
      return of(null);
    }
  }

  ngOnDestroy(): void {
    this.reset();
  }
}
